/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Smart Threshold Test Utility - Available in browser console
 * Usage: window.testSmartThreshold()
 */

import { calculateSmartThresholdWithHistogram } from './imageAnalysisService';

/**
 * Test smart threshold with sample images
 * Access in browser console: testSmartThreshold()
 */
export const initSmartThresholdTest = () => {
  (window as any).testSmartThreshold = async () => {
    console.log('🧪 Smart Threshold Test Suite');
    console.log('================================\n');

    // Create test images with specific luminance characteristics
    // Using percentile 33 + very aggressive detail (clamped 20-70)
    const testCases = [
      {
        name: 'Dark Logo (Black text)',
        luminance: 50,
        expectedRange: [20, 40],
      },
      {
        name: 'Medium Logo (Gray text)',
        luminance: 128,
        expectedRange: [35, 55],
      },
      {
        name: 'Light Logo (Gold/Yellow - like Nhữ Quynh)',
        luminance: 180,
        expectedRange: [50, 70],
      },
      {
        name: 'Very Light Logo (Pink/Tan)',
        luminance: 200,
        expectedRange: [55, 70],
      },
    ];

    for (const testCase of testCases) {
      console.log(`📝 Test: ${testCase.name}`);
      console.log(`   Expected luminance: ${testCase.luminance}`);
      console.log(`   Expected threshold range: ${testCase.expectedRange[0]}-${testCase.expectedRange[1]}`);

      // Create synthetic test image
      const canvas = document.createElement('canvas');
      canvas.width = 100;
      canvas.height = 100;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.log('   ❌ Failed to create canvas context\n');
        continue;
      }

      // Draw test image with specific luminance
      // For luminance L = 0.299*R + 0.587*G + 0.114*B
      // Create a neutral gray: R=G=B = luminance
      const value = Math.min(255, testCase.luminance);
      ctx.fillStyle = `rgb(${value},${value},${value})`;
      ctx.fillRect(0, 0, 100, 100);

      // Set some white background for more realistic test
      const testImg = canvas.toDataURL('image/png');

      try {
        const threshold = await calculateSmartThresholdWithHistogram(testImg);
        const inRange = threshold >= testCase.expectedRange[0] && threshold <= testCase.expectedRange[1];
        const status = inRange ? '✅' : '⚠️';

        console.log(`   ${status} Calculated threshold: ${threshold}`);
        if (!inRange) {
          console.log(`   ⚠️ WARNING: Outside expected range!`);
        }
      } catch (err) {
        console.log(`   ❌ Error: ${err}\n`);
        continue;
      }

      console.log();
    }

    console.log('🧪 Test Complete!');
    console.log('\n📊 INTERPRETATION (Percentile 33 - Very Aggressive Detail):');
    console.log('- Dark logos (luminance ~50): threshold ~20-40');
    console.log('  → Maximum detail preservation');
    console.log('- Medium logos (luminance ~128): threshold ~35-55');
    console.log('  → Excellent detail balance');
    console.log('- Light logos (luminance ~180, like Nhữ Quynh): threshold ~50-70');
    console.log('  → Much better detail for light images! ✨');
    console.log('- Very light logos (luminance ~200): threshold ~55-70');
    console.log('  → Still preserves some detail despite bright color');
  };

  console.log('✅ Smart Threshold Test Suite loaded!');
  console.log('   Run: testSmartThreshold() to test');
};

// Auto-init when this file is imported
if (typeof window !== 'undefined') {
  initSmartThresholdTest();
}
